import { useCallback, useEffect, useRef } from 'react'
import activityApi from '@/apis/activity'

interface CityOption {
  label: string
  value: string
  children?: CityOption[]
}

export interface LocatedCity {
  codes: string[]
  name: string
}

interface Coordinates {
  latitude: number
  longitude: number
}

interface Props {
  adChannelCode: string
  cityOptions: CityOption[]
}

const getCoordinates = (): Promise<Coordinates | null> => new Promise(resolve => {
  if (!navigator.geolocation) {
    resolve(null)
    return
  }

  navigator.geolocation.getCurrentPosition(
    position => resolve({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    }),
    () => resolve(null),
  )
})

const findLocatedCity = (cityOptions: CityOption[], provinceName?: string, cityName?: string): LocatedCity | null => {
  const province = cityOptions.find(item => item.label === provinceName)
  const city = province?.children?.find(item => item.label === cityName)

  if (!province || !city) {
    return null
  }

  return {
    codes: [province.value, city.value],
    name: `${province.label},${city.label}`,
  }
}

/**
 * useCityLocation — 参照 jurongyi-flow 复刻的资料填写页主动定位与逆地理编码 Hook
 * @returns {object} invalidatePendingLocation, requestLocation 接口
 */
export const useCityLocation = ({ adChannelCode, cityOptions }: Props) => {
  const requestedRef = useRef(false)
  const mountedRef = useRef(true)
  const requestVersionRef = useRef(0)

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
      requestVersionRef.current += 1
    }
  }, [])

  useEffect(() => {
    requestedRef.current = false
    requestVersionRef.current += 1
  }, [adChannelCode])

  const invalidatePendingLocation = useCallback(() => {
    // 人工选择优先于较晚返回的定位结果，避免城市被异步回填覆盖
    requestVersionRef.current += 1
  }, [])

  const requestLocation = useCallback(async(): Promise<LocatedCity | null> => {
    if (requestedRef.current) {
      return null
    }

    requestedRef.current = true
    const requestVersion = requestVersionRef.current + 1
    requestVersionRef.current = requestVersion
    const coordinates = await getCoordinates()

    if (!mountedRef.current || requestVersion !== requestVersionRef.current) {
      return null
    }

    // 用户未授权定位或定位坐标获取失败，不进行自动择城
    if (!coordinates) {
      return null
    }

    const params = {
      adChannelCode,
      location: `${coordinates.latitude},${coordinates.longitude}`,
      coordtype: 'wgs84ll',
    }

    try {
      const result = await activityApi.reverseGeo(params)
      if (!mountedRef.current || requestVersion !== requestVersionRef.current) {
        return null
      }

      const data = result?.data || result
      return findLocatedCity(cityOptions, data?.province, data?.city)
    } catch (_) {
      return null
    }
  }, [adChannelCode, cityOptions])

  return {
    invalidatePendingLocation,
    requestLocation,
  }
}

export default useCityLocation
